import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { Document, pdfjs } from 'react-pdf';
import PDFPage from './components/PDFPage';
import './index.scss';
import { CircularProgressWithLabel } from '../CircularProgressWithLabel';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const prefixCls = 'PDFViewer';

export const PDFViewer = ({ filePath = '' }: { filePath: string }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [numPages, setNumPages] = useState(0);
  // Fixed render scale; pages are fitted via `containerWidth` instead.
  const scale = 1;
  const [containerWidth, setContainerWidth] = useState(800);
  const [visibilities, setVisibilities] = useState<boolean[]>([]);
  const [progress, setProgress] = useState(0);

  const pageRefs = useMemo(
    () => Array.from({ length: numPages }, () => createRef<HTMLDivElement>()),
    [numPages]
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current || numPages === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const updatedVisibilities = [...visibilities];
        let changed = false;

        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-page-index'));
          if (
            index >= 0 &&
            index < numPages &&
            entry.isIntersecting &&
            !updatedVisibilities[index]
          ) {
            updatedVisibilities[index] = true;
            changed = true;
          }
        });

        if (changed) {
          setVisibilities(updatedVisibilities);
        }
      },
      {
        root: containerRef.current,
        rootMargin: '600px 0px',
        threshold: 0.1,
      }
    );

    pageRefs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [numPages, visibilities, pageRefs]);

  const handleLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setVisibilities(Array(numPages).fill(false));
  };

  const handleLoadProgress = ({
    loaded,
    total,
  }: {
    loaded: number;
    total: number;
  }) => {
    setProgress(Math.round((loaded / total) * 100));
  };

  return (
    <div className={prefixCls}>
      <div className={`${prefixCls}-container`}>
        <div ref={containerRef} className={`${prefixCls}-viewer`}>
          <Document
            file={filePath}
            onLoadSuccess={handleLoadSuccess}
            onLoadProgress={handleLoadProgress}
            loading={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '90vh',
                  width: '100%',
                  minHeight: '400px',
                }}
              >
                <CircularProgressWithLabel value={progress} />
              </div>
            }
            className={`${prefixCls}-viewer-Document`}
          >
            {visibilities.map((visible, index) => (
              <PDFPage
                ref={pageRefs[index]}
                key={`page_${index}`}
                pageIndex={index}
                visible={visible}
                scale={scale}
                width={containerWidth}
              />
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
};
